/**
 * Sticky Add-to-Cart bar.
 *
 * Zero duplicated cart/variant logic:
 * - Add-to-cart click triggers the REAL product-form's requestSubmit(),
 *   so Shopify's own <product-form> handles the fetch, cart-drawer /
 *   cart-notification rendering and error handling exactly as usual.
 * - Variant/price/availability state is taken from the theme's existing
 *   PubSub events (variant-change, cart-update, cart-error), the same
 *   events product-info.js and product-form.js already publish.
 * - The price markup itself is re-fetched from this section's own
 *   Liquid render (snippet 'price'), never re-implemented in JS, so
 *   currency formatting/sale badges always match the rest of the site.
 */
(function () {
  if (customElements.get('sticky-atc')) return;

  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.productUrl = this.dataset.productUrl;
        this.button = this.querySelector('.sticky-atc__button');
        this.buttonText = this.querySelector('.sticky-atc__button-text');
        this.priceWrapper = this.querySelector('.sticky-atc__price');
        this.isVisible = false;
        this.isSubmitting = false;
        this.observedTarget = null;
        this.intersectionObserver = null;
        this.priceAbortController = null;
        this.variantChangeUnsubscriber = undefined;
        this.cartUpdateUnsubscriber = undefined;
        this.cartErrorUnsubscriber = undefined;

        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.mainProductInfo = this.getMainProductInfo();
        this.mainSectionId = this.mainProductInfo
          ? this.mainProductInfo.dataset.originalSection || this.mainProductInfo.dataset.section
          : undefined;

        this.ensureObserverTarget();
        this.setupSubscriptions();
        this.button?.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
        this.priceAbortController?.abort();
        this.button?.removeEventListener('click', this.onClick);
      }

      // ---- finding the real, existing add-to-cart form/button --------

      getMainProductInfo() {
        const candidates = Array.from(document.querySelectorAll('product-info'));
        return candidates.find((el) => !el.closest('quick-add-modal')) || candidates[0] || null;
      }

      getRealProductForm() {
        const scope = this.mainProductInfo || document;
        return scope.querySelector('product-form');
      }

      getRealForm() {
        return this.getRealProductForm()?.querySelector('form[data-type="add-to-cart-form"]') || null;
      }

      getRealButton() {
        return this.getRealProductForm()?.querySelector('[type="submit"]') || null;
      }

      // ---- show/hide based on whether the real button is visible ------

      ensureObserverTarget() {
        const realButton = this.getRealButton();
        if (!realButton) return;
        if (this.observedTarget === realButton && document.contains(realButton)) return;

        this.intersectionObserver?.disconnect();
        this.observedTarget = realButton;
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => this.toggleVisibility(!entry.isIntersecting));
          },
          { threshold: 0 }
        );
        this.intersectionObserver.observe(this.observedTarget);
      }

      toggleVisibility(shouldShow) {
        if (shouldShow === this.isVisible) return;
        this.isVisible = shouldShow;
        this.classList.toggle('sticky-atc--visible', shouldShow);
        this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        this.querySelectorAll('button').forEach((el) => {
          if (shouldShow) {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', '-1');
          }
        });
      }

      // ---- reacting to variant changes made in the real variant picker -

      setupSubscriptions() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (this.mainSectionId && event.data.sectionId !== this.mainSectionId) return;
          this.ensureObserverTarget();
          this.handleVariantChange(event.data.variant);
        });

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.endSubmitting());
        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => this.endSubmitting());
      }

      handleVariantChange(variant) {
        this.updateButtonState(variant);
        if (variant && this.priceWrapper && this.productUrl) {
          this.fetchAndUpdatePrice(variant.id);
        }
      }

      updateButtonState(variant) {
        if (!this.button) return;
        const strings = window.variantStrings || {};

        if (!variant) {
          this.button.setAttribute('disabled', 'disabled');
          if (this.buttonText && strings.unavailable) this.buttonText.textContent = strings.unavailable;
          return;
        }

        if (!variant.available) {
          this.button.setAttribute('disabled', 'disabled');
          if (this.buttonText && strings.soldOut) this.buttonText.textContent = strings.soldOut;
        } else {
          this.button.removeAttribute('disabled');
          if (this.buttonText && strings.addToCart) this.buttonText.textContent = strings.addToCart;
        }
      }

      fetchAndUpdatePrice(variantId) {
        const url = `${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`;
        this.priceAbortController?.abort();
        this.priceAbortController = new AbortController();

        fetch(url, { signal: this.priceAbortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const updated = html.querySelector('.sticky-atc__price');
            if (updated) this.priceWrapper.innerHTML = updated.innerHTML;
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          });
      }

      // ---- add to cart: reuse the real form, no custom cart logic ------

      onClick(event) {
        event.preventDefault();
        if (this.isSubmitting || this.button?.hasAttribute('disabled')) return;

        const realForm = this.getRealForm();
        if (!realForm) return;

        this.startSubmitting();
        realForm.requestSubmit();
      }

      startSubmitting() {
        this.isSubmitting = true;
        this.button?.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');
      }

      endSubmitting() {
        if (!this.isSubmitting) return;
        this.isSubmitting = false;
        this.button?.classList.remove('loading');
        this.querySelector('.loading__spinner')?.classList.add('hidden');
      }
    }
  );
})();
